import type { AppMeta, FieldValue, MetaEntity, MetaField } from '@clientline/contracts';
import { capitalize, noun } from '@clientline/contracts/i18n';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import {
  formatDateOnly,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
} from '../i18n/format.ts';
import {
  ApiProblem,
  platformKeys,
  readRetryDelay,
  retryRead,
  SESSION_EXPIRED_CODE,
} from './api.ts';
import { EMPTY_REFS, isLookup, lookupShown, type RefIndex } from './lookup-refs.ts';
import { withNextStepLabels } from './next-step-labels.ts';
import { queryPolicy } from './query-policy.ts';
import { reloginHandles } from './relogin.ts';
import { hasSessionHint, loadMeta, subscribeSessionHint } from './session.ts';

export { hasSessionHint, loadMeta };

/**
 * 401 — сесії немає: повторювати марно, екран веде на вхід. Крім `session_expired`, який
 * перехопив повторний вхід поверх сторінки (`platform/relogin.ts`): тоді екран лишається як є,
 * разом із введеним у формі, а після входу дані перечитуються.
 */
export const isUnauthorized = (error: unknown) =>
  error instanceof ApiProblem &&
  error.status === 401 &&
  !(error.code === SESSION_EXPIRED_CODE && reloginHandles());

/**
 * Перемальовувати лише на ПОЯВУ ознаки (вхід у цій чи іншій вкладці, подія `storage`). Зникнення —
 * це вихід чи 401: тоді вкладка вже йде на вхід (`platform/sign-out.ts`) або показує повторний
 * вхід, а перемальовування після `queryClient.clear()` змусило б екрани знову питати `/meta`.
 */
function subscribeSignIn(listener: () => void): () => void {
  return subscribeSessionHint(() => {
    if (hasSessionHint()) listener();
  });
}

/** Ознака входу як стан React: вхід в іншій вкладці вмикає оболонці `/meta` без перезавантаження. */
export function useSessionHint(): boolean {
  return useSyncExternalStore(subscribeSignIn, hasSessionHint, () => false);
}

/** Запит метаданих платформи: той самий для loader кореня, стартового екрана й хуків. */
export const platformMetaQuery = () =>
  queryOptions({
    queryKey: platformKeys.meta,
    queryFn: ({ signal }) => loadMeta(signal).then(withNextStepLabels),
    ...queryPolicy.meta,
    // Метадані тримають старт застосунку: один повтор (мережа, 5xx, `Retry-After` ≤ 10 с), а не три.
    retry: (count, error) => count < 1 && retryRead(count, error),
    retryDelay: readRetryDelay,
  });

/**
 * Метадані платформи за правами людини; 401 — не ввійшли (оболонка показує «Увійти»).
 * `optional` — для оболонки: запит лише за ознакою входу, інакше — дані з кешу, якщо вони є
 * (ознаку ставить сторінка входу). Екрани платформи вантажать метадані завжди.
 */
export function usePlatformMeta(options: { optional?: boolean } = {}) {
  const hint = useSessionHint();
  return useQuery({
    ...platformMetaQuery(),
    enabled: !options.optional || hint,
  });
}

export function entityOf(meta: AppMeta, key: string): MetaEntity | undefined {
  return meta.entities.find((e) => e.key === key);
}

/**
 * Сутності, які людина створює з палітри й швидкого створення: право `create`, запис, а не
 * довідник, і власна форма: `hostedOnly` додає лише панель господаря (views.md, розд. 4).
 */
export function creatableEntities(meta: AppMeta | undefined): MetaEntity[] {
  return (meta?.entities ?? []).filter(
    (e) => e.canCreate && (e.kind ?? 'record') === 'record' && !e.hostedOnly,
  );
}

/**
 * Чи можна створити запис сутності із цим полем: сутність є в `/meta`, `canCreate` і рівень `edit` на
 * полі. Fail closed: немає сутності чи поля — `false`.
 */
export function canCreateWith(meta: AppMeta, entityKey: string, fieldKey: string): boolean {
  const entity = entityOf(meta, entityKey);
  return Boolean(entity?.canCreate && entity.fields.some((f) => f.key === fieldKey && f.edit));
}

/**
 * Сутності з чергою дублів для людини (DAT-07): записи з правилами дублів у `/meta` і правом
 * `<e>.merge`. Спільне для меню користувача, палітри, списку й сторінки `/duplicates`.
 */
export function mergeQueueEntities(meta: AppMeta | undefined): MetaEntity[] {
  if (!meta) return [];
  const permissions = new Set(meta.permissions);
  return meta.entities.filter(
    (e) =>
      (e.kind ?? 'record') === 'record' &&
      (e.duplicates?.length ?? 0) > 0 &&
      permissions.has(`${e.key}.merge`),
  );
}

/** Ключ системного поля відповідального (`OWNER_FIELD` мови умов; без імпорту мови умов у чанк). */
export const OWNER_KEY = 'owner';

/** Текст `localizedText` мовою інтерфейсу: одна точка вибору мови замість `.uk` по коду. */
export const localText = (text: { uk: string }): string => text.uk;

export function fieldOf(entity: MetaEntity, key: string): MetaField | undefined {
  return entity.fields.find((f) => f.key === key);
}

/** «Демо-задачі», «демо-задачу» — форми слова з метаданих (AG-04). */
export const pluralTitle = (entity: MetaEntity) =>
  capitalize(noun(entity.label.uk, { plural: true }));

/**
 * Значення поля-списку (телефон, пошта, ідентифікатор; Б3) текстом: основне перше, через «; »,
 * додатковий номер — після «доб.». Ключі мультисписку — як є, через «; » (чипи з підписами —
 * тікет E1a-UI). Редагування списків — `ListFieldEditor` (components.md, розд. 4.3).
 */
export function listText(value: FieldValue | undefined): string | null {
  if (!Array.isArray(value)) return null;
  return value
    .map((item) =>
      typeof item === 'string' ? item : item.ext ? `${item.value} доб. ${item.ext}` : item.value,
    )
    .join('; ');
}

/**
 * Значення поля як текст: варіанти — підписом, моменти — датою в поясі глядача. Поле-зв'язок —
 * назвою цілі з `refs` відповіді (`lookup-refs.ts`); без доступу — «Запис недоступний», без
 * елемента — «Інший запис», UUID — ніколи (bec368f0).
 */
export function display(
  field: MetaField | undefined,
  value: FieldValue | undefined,
  refs: RefIndex = EMPTY_REFS,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (isLookup(field)) return lookupShown(field, value, refs)?.text ?? '—';
  // Значення поля-файлу — id об'єктів: у списку й шапці лише кількість (FLD-19).
  if ((field?.type === 'file' || field?.type === 'image') && Array.isArray(value)) {
    return value.length === 0 ? '—' : formatNumber(value.length);
  }
  const list = listText(value);
  if (list !== null) return list === '' ? '—' : list;
  const money = moneyOf(value);
  if (money) return formatMoney(Number(money.amount), money.currency);
  if (!field) return String(value);
  // Варіанти бувають і на тексті: «Тип наступного кроку» (`next-step-labels.ts`).
  if (field.type === 'picklist' || field.options) {
    return field.options?.find((o) => o.key === value)?.label.uk ?? String(value);
  }
  if (field.type === 'datetime' && typeof value === 'string') return formatDateTime(value);
  // «Лише дата» поясу не має: день не зсувається для людей у різних поясах (UX-43).
  if (field.type === 'date' && typeof value === 'string') return formatDateOnly(value);
  // Числа за локаллю (UX-44): `1 234,5`; відсоток — `12,5 %`.
  if (
    (field.type === 'number' || field.type === 'integer' || field.type === 'decimal') &&
    (typeof value === 'number' || typeof value === 'string')
  )
    return formatNumber(value);
  if (field.type === 'percent' && (typeof value === 'number' || typeof value === 'string'))
    return formatPercent(value);
  if (field.type === 'boolean') return value ? 'так' : 'ні';
  return String(value);
}

/**
 * Значення грошового поля (FLD-05): `{ amount, currency }` з API. Показ — `245 000 ₴`, `1 200 USD`
 * (a11y-i18n.md, розд. 3); редактор грошей — окремим тікетом інтерфейсу (Б5-W1).
 */
export function moneyOf(
  value: FieldValue | undefined,
): { amount: string; currency: string } | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

/** Класи, значення яких можна класти в чернетку вкладки; решта — ні (allow-list). */
const DRAFTABLE_CLASSES: ReadonlySet<string> = new Set(['public', 'internal', 'personal']);

/**
 * Значення поля не кладемо в чернетку вкладки (SEC-047, SEC-169; states.md, розд. 7, умова 4),
 * а незбережену зміну захищає діалог UX-27. Fail closed: чернетка лише в поля, чий клас з `/meta`
 * (`classification`) відомий і не `sensitive`. Поля немає в метаданих, класу немає (стара збірка
 * сервера, кеш) чи клас невідомий (набір розширюваний) — це `sensitive`.
 */
export function isSensitive(field: MetaField | undefined): boolean {
  const cls: unknown = field?.classification;
  return typeof cls !== 'string' || !DRAFTABLE_CLASSES.has(cls);
}

/**
 * Значення для чернетки вкладки (умова 4 Security з #209, умова #233): лише поля, чий клас з `/meta`
 * відомий і не `sensitive` (`isSensitive`, fail closed). `describe` дає опис поля за ключем значення:
 * поле самої сутності чи поле сутності, з якого бере значення поле форми. `withheld` — щось не
 * пішло в чернетку: перехід тоді має зупиняти діалог UX-27.
 */
export function draftable<V>(
  values: Readonly<Record<string, V>>,
  describe: (key: string) => MetaField | undefined,
): { values: Record<string, V>; withheld: boolean } {
  const kept: Record<string, V> = {};
  let withheld = false;
  for (const [key, value] of Object.entries(values)) {
    if (isSensitive(describe(key))) withheld = true;
    else kept[key] = value;
  }
  return { values: kept, withheld };
}

export function toneOf(field: MetaField | undefined, value: FieldValue | undefined) {
  return field?.options?.find((o) => o.key === value)?.tone;
}

/** Типи контактних полів (контракт Б3): їх значень немає в заголовку вкладки й недавніх. */
const CONTACT_TYPES: ReadonlySet<string> = new Set(['phone', 'email']);

/**
 * Назва запису для заголовка вкладки й рядка «Недавні» (UX-07): поля заголовка з метаданих, крім
 * `sensitive` і контактних — заголовок потрапляє в історію браузера. Порожньо — `undefined`.
 */
export function recordTitle(
  entity: MetaEntity,
  values: Record<string, FieldValue | undefined>,
  refs: RefIndex = EMPTY_REFS,
): string | undefined {
  const parts: string[] = [];
  for (const key of entity.titleFields) {
    const field = fieldOf(entity, key);
    const value = values[key];
    if (!field || isSensitive(field) || CONTACT_TYPES.has(field.type)) continue;
    if (value === null || value === undefined || value === '') continue;
    // Назви цілі немає (немає `refs` чи вона недоступна): слово-заглушка в назві не потрібне (К12).
    if (isLookup(field) && lookupShown(field, value, refs)?.kind !== 'link') continue;
    parts.push(display(field, value, refs));
  }
  return parts.length > 0 ? parts.join(' — ') : undefined;
}
